import { useCallback, useEffect, useState } from "react"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Loader2, RefreshCw } from "lucide-react"
import { ArmResultsTable } from "@/features/outreach/components/ArmResultsTable"
import type { ArmResults } from "@/features/outreach/types/outreach.types"
import * as programApi from "../../api/outreach-program.api"
import { apiErrorMessage, formatCentral, todayCentral } from "../../lib/program"
import type { PlanEntries, ProgramStatus, TickRecord } from "../../types/outreach-program.types"
import { ProgramControls, ProgramStatusBadge } from "./ProgramControls"
import { ProgramConfigForm } from "./ProgramConfigForm"
import { CapUsagePanel, LastTick, NotAvailable, StateBreakdownTable, TickTable } from "./ProgramPanels"
import { PlanList } from "./PlanList"
import { RestartMaxRetries } from "./RestartMaxRetries"

interface Props {
  orgId: string
  /** The pipeline's per-state counts (patient-lifecycle-summary), shown next to the program's breakdown. */
  stateCounts: Record<string, number>
  /** Called with the new status after any change, so the page (Start N labels) stays in step. */
  onProgramChange: (p: ProgramStatus) => void
  onRefresh: () => void
}

const errMsg = (r: PromiseSettledResult<unknown>) =>
  r.status === "rejected" ? apiErrorMessage(r.reason, "failed") : null

export function ClinicProgramTab({ orgId, stateCounts, onProgramChange, onRefresh }: Props) {
  const [program, setProgram] = useState<ProgramStatus | null>(null)
  const [programError, setProgramError] = useState<string | null>(null)
  const [ticks, setTicks] = useState<TickRecord[] | null>(null)
  const [plan, setPlan] = useState<PlanEntries | null>(null)
  const [planDate, setPlanDate] = useState(() => todayCentral())
  const [arms, setArms] = useState<ArmResults | null>(null)
  const [loading, setLoading] = useState(false)

  const load = useCallback(async () => {
    setLoading(true)
    const [p, t, a] = await Promise.allSettled([
      programApi.getProgram(orgId),
      programApi.getProgramTicks(orgId, 20),
      programApi.getProgramArms(orgId),
    ])
    if (p.status === "fulfilled") {
      setProgram(p.value)
      setProgramError(null)
      onProgramChange(p.value)
    } else {
      setProgram(null)
      setProgramError(errMsg(p))
    }
    setTicks(t.status === "fulfilled" ? t.value : null)
    setArms(a.status === "fulfilled" ? a.value : null)
    setLoading(false)
  }, [orgId, onProgramChange])

  const loadPlan = useCallback(async () => {
    try {
      setPlan(await programApi.getProgramPlan(orgId, planDate))
    } catch {
      setPlan(null)
    }
  }, [orgId, planDate])

  useEffect(() => {
    load()
  }, [load])

  useEffect(() => {
    loadPlan()
  }, [loadPlan])

  const changed = (p: ProgramStatus) => {
    setProgram(p)
    onProgramChange(p)
    onRefresh()
    programApi.getProgramTicks(orgId, 20).then(setTicks, () => setTicks(null))
    loadPlan()
  }

  if (!program) {
    return (
      <div className="py-12 text-center text-sm text-muted-foreground">
        {loading ? (
          <Loader2 className="mx-auto h-5 w-5 animate-spin" />
        ) : (
          <>
            <p>Clinic program: not available{programError ? ` (${programError})` : ""}.</p>
            <Button variant="outline" size="sm" className="mt-3" onClick={load}>
              Retry
            </Button>
          </>
        )}
      </div>
    )
  }

  return (
    <div className="space-y-5">
      <Card>
        <CardHeader className="pb-3">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <CardTitle className="flex items-center gap-2 text-base">
              Clinic program <ProgramStatusBadge program={program} />
            </CardTitle>
            <Button variant="ghost" size="sm" onClick={load} disabled={loading} aria-label="Reload program">
              {loading ? <Loader2 className="animate-spin" /> : <RefreshCw />}
            </Button>
          </div>
          <CardDescription>
            {program.status === "enabled" && program.mode === "live" && "The 10-minute tick books consent and check-in calls and texts."}
            {program.status === "enabled" && program.mode === "shadow" && "Shadow: the tick records its plan below and books nothing."}
            {program.status === "paused" &&
              `Paused${program.pause?.reason ? `: ${program.pause.reason}` : ""}${program.pause?.since ? ` (since ${formatCentral(program.pause.since)})` : ""}. Pending calls and texts are skipped when due; the tick only plans.`}
            {program.status === "disabled" && "Disabled: the tick does nothing for this clinic. Start N buttons run once as manual runs."}
            {program.updated_by && ` Last changed by ${program.updated_by}${program.updated_at ? ` ${formatCentral(program.updated_at)}` : ""}.`}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <ProgramControls orgId={orgId} program={program} onChanged={changed} />
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            <div className="rounded-lg border p-3">
              <LastTick tick={program.last_tick} />
            </div>
            <div className="rounded-lg border p-3">
              <CapUsagePanel usage={program.cap_usage} />
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Per-state breakdown</CardTitle>
          <CardDescription>
            Active members only. "Pipeline" is the count on the Pipeline tab; it should equal "Total".
          </CardDescription>
        </CardHeader>
        <CardContent>
          <StateBreakdownTable breakdown={program.state_breakdown} stateCounts={stateCounts} />
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Plan</CardTitle>
          <CardDescription>
            What the program plans for each patient on a day (Central), updated by every tick. "Intended bookings" are
            the calls the latest tick booked or would book; the next tick recomputes them.
            {program.status === "enabled" && program.mode === "shadow" && " Shadow: nothing is booked; this is what live would book."}
            {program.status === "paused" && " Paused: nothing is booked; this is what would be booked."}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <PlanList plan={plan} date={planDate} onDateChange={setPlanDate} />
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Settings</CardTitle>
        </CardHeader>
        <CardContent>
          <ProgramConfigForm orgId={orgId} program={program} onSaved={changed} />
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Reminder A/B results</CardTitle>
        </CardHeader>
        <CardContent>{arms ? <ArmResultsTable results={arms} /> : <NotAvailable what="A/B results" />}</CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Recent ticks</CardTitle>
        </CardHeader>
        <CardContent>
          <TickTable ticks={ticks} />
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Restart max-retries patients</CardTitle>
          <CardDescription>
            Patients in CONSENT_MAX_RETRIES_REACHED are never re-enrolled by the program. Restart a cohort to give them
            a new attempt cycle. Preview first; nothing changes until you confirm.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <RestartMaxRetries
            orgId={orgId}
            onApplied={() => {
              load()
              onRefresh()
            }}
          />
        </CardContent>
      </Card>
    </div>
  )
}
